import React, { useState, useEffect } from "react";
import axios from "axios";
import Dropzone from "react-dropzone";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

import { SingleImageDropzone } from "../DropzoneComponents";

// ---- Tree Dropdown Component ---- //
const TreeDropdown = ({ items, selected, onSelect }) => {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen(!open);
  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, def) => translations[key] || def;

  const renderTree = (nodes, level = 0) => {
    return nodes.map((node) => {
      const hasChildren = node.children && node.children.length > 0;
      const isChild = level > 0;
      return (
        <div key={node.id}>
          <div
            onClick={() => {
              onSelect(node.id);
              setOpen(false);
            }}
            style={{
              padding: "6px 10px",
              paddingLeft: `${level * 22 + 10}px`,
              cursor: "pointer",
              background: node.id === selected ? "#dceeff" : "white",
              fontWeight: node.id === selected ? "600" : "400",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {isChild && <span style={{ fontSize: 16, color: "#444" }}>↳</span>}
            <span>{node.title}</span>
          </div>
          {hasChildren && renderTree(node.children, level + 1)}
        </div>
      );
    });
  };

  return (
    <div className="dropdown w-100 position-relative">
      <button
        className="btn btn-outline-secondary w-100 text-start"
        type="button"
        onClick={toggle}
      >
        {selected
          ? getText("SELECTED_LABEL") +
            " " +
            items.find((i) => i.id === selected)?.title
          : getText("SELECT_PARENT_PLACEHOLDER")}
      </button>

      {open && (
        <div
          className="border rounded bg-white w-100 shadow-sm mt-1"
          style={{
            maxHeight: 300,
            overflowY: "auto",
            position: "absolute",
            zIndex: 999,
          }}
        >
          {renderTree(buildTree(items))}
        </div>
      )}
    </div>
  );
};

const buildTree = (items, parent = null) =>
  items
    .filter((i) => i.parent_id === parent)
    .map((i) => ({ ...i, children: buildTree(items, i.id) }));

const CategoryCreate = () => {
  const navigate = useNavigate();

  const [parents, setParents] = useState([]);
  const [brands, setBrands] = useState([]);
  const [settings, setSettings] = useState(null);
  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, def) => translations[key] || def;

  const [form, setForm] = useState({
    title: "",
    parent_id: null,
    is_main_category: "yes",
    brand_option: "hayir",
    brand_ids: [],
    seo_title: "",
    seo_description: "",
    seo_keywords: "",
    status: "aktif",
  });

  const [file, setFile] = useState(null);
  const [content, setContent] = useState("");

  //  Fetch categories & brands
  useEffect(() => {
    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/stock-categories/list-simple`,
      )
      .then((res) => setParents(res.data))
      .catch(() => toast.error(getText("CATEGORY_LIST_FETCH_ERROR")));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setSettings(res.data);
      })
      .catch(() => toast.error(getText("SETTINGS_FETCH_ERROR")));
  }, []);

  //  Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = new FormData();
    data.append("title", form.title);
    data.append(
      "parent_id",
      form.is_main_category === "yes" ? "" : form.parent_id,
    );
    data.append("content", content);
    data.append("seo_title", form.seo_title);
    data.append("seo_description", form.seo_description);
    data.append("seo_keywords", form.seo_keywords);
    data.append("brand_option", form.brand_option);
    data.append("brand_ids", JSON.stringify(form.brand_ids));
    data.append("status", form.status);

    if (file) data.append("image", file);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/stock-categories/create`,
        data,
      );
      toast.success(getText("CATEGORY_CREATED_SUCCESS"));
      navigate("/categories/table");
    } catch (error) {
      console.log(error);
      toast.error(getText("CATEGORY_CREATE_FAILED"));
    }
  };

  return (
    <div className="container mt-4">
      <form className="card p-4" onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">
            {" "}
            {getText("CATEGORY_NAME_LABEL")}
          </label>
          <input
            className="form-control"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
          />
        </div>

        {/* Ana / Alt seçim */}
        <label className="form-label fw-bold">
          {getText("CATEGORY_POSITION_LABEL")}
        </label>
        <select
          className="form-select mb-2"
          value={form.is_main_category}
          onChange={(e) =>
            setForm({ ...form, is_main_category: e.target.value })
          }
        >
          <option value="yes"> {getText("MAIN_CATEGORY_OPTION")}</option>
          <option value="no"> {getText("SUB_CATEGORY_OPTION")}</option>
        </select>

        {form.is_main_category === "no" && (
          <div className="mb-3">
            <label className="form-label">
              {getText("SELECT_PARENT_CATEGORY")}
            </label>
            <TreeDropdown
              items={parents}
              selected={form.parent_id}
              onSelect={(id) => setForm({ ...form, parent_id: id })}
            />
          </div>
        )}

        {/* Görsel */}
        <div className="mb-3">
          <label className="form-label">
            {getText("CATEGORY_IMAGE_LABEL")} ({settings?.category_image_width}x
            {settings?.category_image_height})
          </label>

          {settings ? (
            <SingleImageDropzone
              fieldName="image"
              fieldValue={file}
              setFieldValues={(updater) => {
                const newFile =
                  typeof updater === "function"
                    ? updater({ image: file }).image
                    : updater.image;
                setFile(newFile);
              }}
              required={true}
              maxWidth={Number(settings.category_image_width)}
              maxHeight={Number(settings.category_image_height)}
            />
          ) : (
            <p>{getText("SETTINGS_LOADING")}</p>
          )}
        </div>
        <label className="form-label"> {getText("CONTENT_LABEL")}</label>
        <Editor
          tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.6.1/tinymce.min.js"
          value={content}
          onEditorChange={setContent}
          init={{
            promotion: false,
            height: 400,
            plugins: [
              "advlist",
              "autolink",
              "link",
              "image",
              "lists",
              "charmap",
              "preview",
              "anchor",
              "pagebreak",
              "searchreplace",
              "wordcount",
              "visualblocks",
              "code",
              "fullscreen",
              "insertdatetime",
              "media",
              "table",
              "emoticons",
              "help",
            ],
            toolbar:
              "undo redo | styles | bold italic | alignleft aligncenter alignright alignjustify | " +
              "bullist numlist outdent indent | link image | " +
              "print preview media fullscreen code | forecolor backcolor emoticons | help",
            content_style:
              "body { font-family:Helvetica,Arial,sans-serif; font-size:16px }",
            image_advtab: true,
            automatic_uploads: false,
            images_upload_url: `${process.env.REACT_APP_API_BASE_URL}/api/module-data/upload`,
            images_upload_handler: null,
          }}
        />

        {/* SEO */}
        <div className="mt-3">
          <label className="form-label fw-bold">
            {" "}
            {getText("SEO_TITLE_LABEL")}
          </label>
          <input
            className="form-control"
            value={form.seo_title}
            onChange={(e) => setForm({ ...form, seo_title: e.target.value })}
          />
        </div>

        <div className="mt-2">
          <label className="form-label fw-bold">
            {" "}
            {getText("SEO_DESCRIPTION_LABEL")}
          </label>
          <textarea
            className="form-control"
            rows="2"
            value={form.seo_description}
            onChange={(e) =>
              setForm({ ...form, seo_description: e.target.value })
            }
          />
        </div>

        <div className="mt-2">
          <label className="form-label fw-bold">
            {getText("SEO_KEYWORDS_LABEL")}
          </label>
          <input
            className="form-control"
            value={form.seo_keywords}
            onChange={(e) => setForm({ ...form, seo_keywords: e.target.value })}
          />
        </div>
        <div className="mt-3">
          <label className="form-label fw-bold">
            {" "}
            {getText("BRAND_SELECTION_LABEL")}
          </label>
          <select
            className="form-select"
            value={form.brand_option}
            onChange={(e) => setForm({ ...form, brand_option: e.target.value })}
          >
            <option value="hayir"> {getText("NO_LABEL")}</option>
            <option value="evet"> {getText("YES_LABEL")}</option>
          </select>
        </div>

        {/* Status */}
        <div className="mt-3">
          <label className="form-label fw-bold">
            {" "}
            {getText("STATUS_LABEL")}
          </label>
          <select
            className="form-select"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            <option value="aktif">{getText("STATUS_ACTIVE")}</option>
            <option value="pasif">{getText("STATUS_PASSIVE")}</option>
          </select>
        </div>

        <div className="d-flex justify-content-center">
          <button className="btn btn-primary"> {getText("SAVE")}</button>
        </div>
      </form>
    </div>
  );
};

export default CategoryCreate;
